import React, { useState } from 'react';
import Box from '../../components/Common/Box';
import Disclaimer from '../../components/Common/Disclaimer';

interface MobileAlphaFusionProps {
    data: any;
    activeTab: 'opportunities' | 'sell' | 'universe' | 'info';
    setActiveTab: (tab: 'opportunities' | 'sell' | 'universe' | 'info') => void;
    filters: any;
    setFilters: (filters: any) => void;
    opportunities: any[];
    sellSignals: any[];
    filteredUniverse: any[];
    sentiment: string;
    sentimentColors: Record<string, { bg: string; color: string }>;
    navigate: (path: string) => void;
}

const MobileAlphaFusion: React.FC<MobileAlphaFusionProps> = ({
    data,
    activeTab,
    setActiveTab,
    filters,
    setFilters,
    opportunities,
    sellSignals,
    filteredUniverse,
    sentiment,
    sentimentColors,
    navigate
}) => {
    const [showFilters, setShowFilters] = useState(false);

    const tabs: { key: 'opportunities' | 'sell' | 'universe' | 'info'; label: string; color: string }[] = [
        { key: 'opportunities', label: `🔥 Entrée (${opportunities.length})`, color: '#7c3aed' },
        { key: 'sell', label: `📉 Sortie (${sellSignals.length})`, color: '#dc2626' },
        { key: 'universe', label: `🌍 Univers`, color: '#2563eb' },
        { key: 'info', label: `ℹ️ Stratégie`, color: '#0ea5e9' },
    ];

    return (
        <div style={{ paddingBottom: '30px', background: 'var(--bg-main)' }}>
            <Disclaimer type="investment" />

            {/* Hero Summary */}
            <div style={{ padding: '20px', background: 'var(--bg-box)', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'center', gap: '24px', alignItems: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>🎯 Signaux Alpha</div>
                    <div style={{ fontSize: '28px', fontWeight: '900', color: 'var(--text-heading)' }}>{opportunities.length}</div>
                </div>
                <div style={{ width: '1px', height: '40px', background: 'var(--border-color)' }} />
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>📈 Sentiment</div>
                    <span style={{
                        padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: '700',
                        backgroundColor: sentimentColors[sentiment]?.bg || 'var(--bg-input)',
                        color: sentimentColors[sentiment]?.color || 'var(--text-muted)',
                    }}>
                        {sentiment === 'BULLISH' ? '🟢 Haussier' : sentiment === 'BEARISH' ? '🔴 Baissier' : '⚪ Neutre'}
                    </span>
                </div>
            </div>

            {/* Sticky Segmented Control (scrollable) */}
            <div style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--bg-main)', borderBottom: '1px solid var(--border-color)', padding: '12px 16px' }}>
                <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
                    {tabs.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            style={{
                                flexShrink: 0, padding: '10px 16px', borderRadius: '10px', fontSize: '13px', fontWeight: '600',
                                background: activeTab === tab.key ? tab.color : 'var(--bg-input)',
                                color: activeTab === tab.key ? '#ffffff' : 'var(--text-muted)',
                                border: 'none', transition: 'all 0.2s', touchAction: 'manipulation', whiteSpace: 'nowrap',
                                minHeight: '44px',
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content Area */}
            <div style={{ padding: '16px' }}>

                {/* ============ OPPORTUNITIES ============ */}
                {activeTab === 'opportunities' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {opportunities.length === 0 ? (
                            <Box>
                                <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                                    <p style={{ fontSize: '16px' }}>Aucune configuration Alpha actuellement.</p>
                                    <p style={{ fontSize: '13px', marginTop: '8px' }}>Patience, la Smart Money accumule. 🧘</p>
                                </div>
                            </Box>
                        ) : (
                            opportunities.map((opp: any) => (
                                <Box key={opp.symbole}>
                                    <div style={{ padding: '16px' }}>
                                        {/* Header: Symbol + Score */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                            <div>
                                                <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#60a5fa', cursor: 'pointer' }} onClick={() => navigate(`/data?symbol=${opp.symbole}`)}>
                                                    {opp.symbole}
                                                </span>
                                                <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '2px 0 0 0' }}>{opp.nom_societe}</p>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <div style={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: 16, backgroundColor: opp.score >= 150 ? '#ec4899' : '#7c3aed' }}>
                                                    {opp.score}
                                                </div>
                                                <span style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>Score</span>
                                            </div>
                                        </div>

                                        {/* Price Grid 2x2 */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px', padding: '12px', backgroundColor: 'var(--bg-input)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                                            <div>
                                                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Prix</span>
                                                <span style={{ display: 'block', fontSize: 15, fontWeight: '700', color: 'var(--text-heading)' }}>{opp.entry_price?.toLocaleString('fr-FR')}</span>
                                            </div>
                                            <div>
                                                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>NVI (Smart Money)</span>
                                                <span style={{ display: 'block', fontSize: 15, fontWeight: '700', color: opp.nvi_bullish ? '#16a34a' : '#9ca3af' }}>
                                                    {opp.nvi_bullish ? '🟢 Strong' : '⚪ Weak'}
                                                </span>
                                            </div>
                                            <div>
                                                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Guppy (Trend)</span>
                                                <span style={{ display: 'block', fontSize: 15, fontWeight: '700', color: opp.guppy_bullish ? '#4ade80' : '#9ca3af' }}>
                                                    {opp.guppy_bullish ? '🟢 Bull' : '🔴 Bear'}
                                                </span>
                                            </div>
                                            <div>
                                                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>TRIX</span>
                                                <span style={{ display: 'block', fontSize: 15, fontWeight: '700', color: '#2563eb' }}>
                                                    {opp.trix?.toFixed(1)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Badges */}
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                                            {opp.is_bull_flag && (
                                                <span style={{ fontSize: 12, backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', padding: '3px 10px', borderRadius: 6, fontWeight: 600 }}>
                                                    🏳️ Bull Flag
                                                </span>
                                            )}
                                        </div>

                                        {/* Justification */}
                                        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0, fontStyle: 'italic', lineHeight: '1.5' }}>{opp.justification}</p>
                                    </div>
                                </Box>
                            ))
                        )}
                    </div>
                )}

                {/* ============ SELL SIGNALS ============ */}
                {activeTab === 'sell' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {sellSignals.length === 0 ? (
                            <Box>
                                <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                                    <p style={{ fontSize: '16px' }}>Aucune alerte de retournement. ✅</p>
                                    <p style={{ fontSize: '13px', marginTop: '8px' }}>Vos positions sont en tendance haussière.</p>
                                </div>
                            </Box>
                        ) : (
                            sellSignals.map((signal: any) => (
                                <Box key={signal.symbole}>
                                    <div style={{ padding: '16px' }}>
                                        {/* Header */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                            <div>
                                                <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#60a5fa', cursor: 'pointer' }} onClick={() => navigate(`/data?symbol=${signal.symbole}`)}>
                                                    {signal.symbole}
                                                </span>
                                                <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '2px 0 0 0' }}>{signal.nom_societe}</p>
                                            </div>
                                            <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 'bold', backgroundColor: 'rgba(220, 38, 38, 0.1)', padding: '4px 10px', borderRadius: 6 }}>
                                                SORTIE ⚠️
                                            </span>
                                        </div>

                                        {/* Data Grid */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '12px', padding: '12px', backgroundColor: 'var(--bg-input)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                                            <div>
                                                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Prix</span>
                                                <span style={{ display: 'block', fontSize: 14, fontWeight: '700', color: 'var(--text-heading)' }}>{signal.current_price?.toLocaleString('fr-FR')}</span>
                                            </div>
                                            <div style={{ textAlign: 'center' }}>
                                                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>P&L</span>
                                                <span style={{ display: 'block', fontSize: 14, fontWeight: '700', color: signal.unrealized_pnl_pct >= 0 ? '#4ade80' : '#f87171' }}>
                                                    {signal.unrealized_pnl_pct >= 0 ? '+' : ''}{signal.unrealized_pnl_pct?.toFixed(2)}%
                                                </span>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Qté</span>
                                                <span style={{ display: 'block', fontSize: 14, fontWeight: '700', color: 'var(--text-heading)' }}>{signal.quantity_held}</span>
                                            </div>
                                        </div>

                                        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 8px 0', fontStyle: 'italic', lineHeight: '1.5' }}>{signal.justification}</p>
                                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                            EMA30 ({signal.ema_30?.toFixed(0)}) {'<'} EMA60 ({signal.ema_60?.toFixed(0)})
                                        </div>
                                    </div>
                                </Box>
                            ))
                        )}
                    </div>
                )}

                {/* ============ UNIVERSE ============ */}
                {activeTab === 'universe' && (
                    <div>
                        {/* Search */}
                        <input
                            type="text"
                            placeholder="🔍 Rechercher un ticker..."
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border-color)', fontSize: '14px', outline: 'none', backgroundColor: 'var(--bg-input)', color: 'var(--text-main)', marginBottom: '12px', boxSizing: 'border-box' }}
                        />

                        {/* Filter Chips Row (scrollable) */}
                        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', paddingBottom: '12px', marginBottom: '12px' }}>
                            {[
                                { key: 'nviOnly', label: '🟢 NVI', active: filters.nviOnly },
                                { key: 'guppyOnly', label: '🔵 Guppy', active: filters.guppyOnly },
                                { key: 'bullFlagOnly', label: '🏳️ Flag', active: filters.bullFlagOnly },
                                { key: 'trixPositive', label: '📈 TRIX>0', active: filters.trixPositive },
                                { key: 'highScore', label: '✨ Score>150', active: filters.highScore },
                                { key: 'undervaluedOnly', label: '💎 Décote', active: filters.undervaluedOnly },
                            ].map(f => (
                                <button
                                    key={f.key}
                                    onClick={() => setFilters({ ...filters, [f.key]: !f.active })}
                                    style={{
                                        flexShrink: 0, padding: '8px 14px', borderRadius: 20, fontSize: 12, fontWeight: f.active ? 'bold' : 'normal',
                                        border: f.active ? '1px solid #38bdf8' : '1px solid var(--border-color)',
                                        backgroundColor: f.active ? 'rgba(56, 189, 248, 0.1)' : 'var(--bg-box)',
                                        color: f.active ? '#38bdf8' : 'var(--text-muted)',
                                        cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap', minHeight: '36px',
                                    }}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>

                        {/* Universe Cards */}
                        <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                            {filteredUniverse.length} action{filteredUniverse.length > 1 ? 's' : ''} trouvée{filteredUniverse.length > 1 ? 's' : ''}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {filteredUniverse.map((stock: any) => {
                                const isTitan = stock.status === 'ALPHA_MODE';
                                const isOwned = stock.is_owned;
                                return (
                                    <Box key={stock.symbole}>
                                        <div style={{
                                            padding: '14px 16px',
                                            borderLeft: isTitan ? '3px solid #4ade80' : isOwned ? '3px solid #38bdf8' : '3px solid transparent',
                                        }}>
                                            {/* Row 1: Ticker + Score */}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#60a5fa', cursor: 'pointer' }} onClick={() => navigate(`/data?symbol=${stock.symbole}`)}>
                                                    {stock.symbole}
                                                </span>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    {isTitan && <span style={{ fontSize: 11, color: '#4ade80', fontWeight: 'bold' }}>TITAN 🚀</span>}
                                                    <span style={{ fontSize: '15px', fontWeight: 'bold', color: 'var(--text-heading)', backgroundColor: 'var(--bg-input)', padding: '2px 10px', borderRadius: '12px' }}>
                                                        {stock.score}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Row 2: Prix + Décote */}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                <span style={{ fontSize: '14px', color: 'var(--text-main)' }}>
                                                    {stock.cours_actuel?.toLocaleString('fr-FR')} <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>FCFA</span>
                                                </span>
                                                {stock.valuation_upside ? (
                                                    <span style={{ fontSize: 13, fontWeight: stock.valuation_upside > 10 ? 'bold' : 'normal', color: stock.valuation_upside > 0 ? '#4ade80' : '#f87171' }}>
                                                        {stock.valuation_upside > 0 ? '+' : ''}{stock.valuation_upside.toFixed(0)}% décote
                                                    </span>
                                                ) : (
                                                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>-</span>
                                                )}
                                            </div>

                                            {/* Row 3: Indicator Badges */}
                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, backgroundColor: stock.nvi_bullish ? 'rgba(22,163,74,0.12)' : 'var(--bg-input)', color: stock.nvi_bullish ? '#4ade80' : 'var(--text-muted)' }}>
                                                    NVI {stock.nvi_bullish ? '🟢' : '⚪'}
                                                </span>
                                                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, backgroundColor: stock.guppy_bullish ? 'rgba(37,99,235,0.12)' : 'var(--bg-input)', color: stock.guppy_bullish ? '#60a5fa' : 'var(--text-muted)' }}>
                                                    Guppy {stock.guppy_bullish ? '🟢' : '🔴'}
                                                </span>
                                                {stock.is_bull_flag && (
                                                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, backgroundColor: 'rgba(245,158,11,0.12)', color: '#fbbf24' }}>
                                                        🏳️ Flag
                                                    </span>
                                                )}
                                                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, backgroundColor: 'var(--bg-input)', color: '#2563eb' }}>
                                                    TRIX {stock.trix?.toFixed(1)}
                                                </span>
                                            </div>
                                        </div>
                                    </Box>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ============ STRATEGY INFO ============ */}
                {activeTab === 'info' && (
                    <Box>
                        <div style={{ padding: '20px' }}>
                            <h3 style={{ marginTop: 0, fontSize: '18px' }}>🚀 Stratégie : Indicateurs de momentum</h3>
                            <p style={{ fontSize: '14px', lineHeight: '1.6', color: 'var(--text-main)' }}>
                                Une méthode basée sur des indicateurs de momentum (flux de niveau institutionnel, structure de continuation et tendance de fond).
                            </p>

                            <h4 style={{ marginTop: 20, fontSize: '15px' }}>1. Smart Money (NVI)</h4>
                            <p style={{ fontSize: '13px', lineHeight: '1.6', color: 'var(--text-main)' }}>
                                Le <strong>Negative Volume Index (NVI)</strong> détecte ce que font les "Mains Fortes" quand le public se désintéresse.<br />
                                Si <strong>NVI {'>'} Moyenne(255)</strong>, les pros accumulent en silence.
                            </p>

                            <h4 style={{ marginTop: 20, fontSize: '15px' }}>2. Tendance Lourde (Guppy)</h4>
                            <p style={{ fontSize: '13px', lineHeight: '1.6', color: 'var(--text-main)' }}>
                                On n'achète que si la tendance de fond est haussière.<br />
                                Signal : <strong>EMA 30 {'>'} EMA 60</strong>.
                            </p>

                            <h4 style={{ marginTop: 20, fontSize: '15px' }}>3. Priorisation (Bull Flag)</h4>
                            <p style={{ fontSize: '13px', lineHeight: '1.6', color: 'var(--text-main)' }}>
                                On privilégie les actions qui cassent un drapeau de consolidation.
                            </p>

                            <h4 style={{ marginTop: 20, fontSize: '15px' }}>🔴 Sortie (Exit)</h4>
                            <p style={{ fontSize: '13px', lineHeight: '1.6', color: 'var(--text-main)' }}>
                                On sort uniquement quand la <strong>Tendance Lourde casse</strong> (EMA 30 {'<'} EMA 60). Pas de sortie sur panique.
                            </p>
                        </div>
                    </Box>
                )}
            </div>
        </div>
    );
};

export default MobileAlphaFusion;
